/**
 * UseTestValidation
 *
 * Hook providing validation logic for service test form submission.
 * Reviewed during SPLIT-08 refactoring (Phase 6). Retained as single file: 228 lines with
 * a linear validation chain across 4 domains. The 5 schedule utility functions serve only
 * this hook and form a cohesive single-concern module.
 */
import { useCallback } from 'react';
import { get, isEmpty } from 'lodash';
import { t } from '@lingui/macro';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../../../stores/AlertsStore';
import { deepMergeForSave } from '../TestDetailMergeUtils';
import { DAILY, INTERVAL, WINDOW } from '../../ServiceTestsScheduleConstants';
import { MULTI } from '../../../general/Constants';

const DAY_IN_SECONDS = 86400;

/**
 * Convert "HH:mm" time string to total seconds since midnight.
 * Returns null for malformed input so callers can treat it as "not supplied".
 * @param {string} time
 * @returns {number|null}
 */
function timeToSeconds(time) {
    if (typeof time !== 'string') return null;
    const parts = time.split(':');
    if (parts.length < 2) return null;
    const hours = Number.parseInt(parts[0], 10);
    const minutes = Number.parseInt(parts[1], 10);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 3600 + minutes * 60;
}

/**
 * Calculate duration in seconds between two "HH:mm" times.
 * Handles midnight crossing by adding one day when end <= start.
 * Matches portal/ui's getDurationAsSeconds() behavior.
 * @param {string} startTime
 * @param {string} endTime
 * @returns {number}
 */
function getDurationInSeconds(startTime, endTime) {
    const startSec = timeToSeconds(startTime);
    const endSec = timeToSeconds(endTime);
    if (startSec == null || endSec == null) return 0;
    return endSec <= startSec ? endSec + DAY_IN_SECONDS - startSec : endSec - startSec;
}

/**
 * Check if a schedule unit value has been supplied (non-null, non-undefined, non-blank).
 * Matches portal/ui's isScheduleUnitSupplied().
 * @param {*} value
 * @returns {boolean}
 */
function isSupplied(value) {
    if (value == null) return false;
    return value.toString().trim() !== '';
}

/**
 * Resolve schedule state from merged data.
 * Prefers flat form fields (set by ScheduleFields component) over the nested
 * scheduling object (loaded from API). This ensures we validate the latest
 * user edits when flat fields are present, and fall back to API data otherwise.
 *
 * @param {Object} mergedData
 * @returns {{ scheduleType: string, interval: *, isWindow: boolean, windowStart: *, windowEnd: *, dailyStart: * }}
 */
function resolveScheduleState(mergedData) {
    const scheduling = mergedData.scheduling || {};
    const apiType = scheduling.scheduleType || scheduling.type;

    const scheduleType = mergedData.scheduleType ?? (apiType === DAILY ? DAILY : INTERVAL);
    const isWindow = mergedData.isWindow ?? scheduling.isWindow ?? apiType === WINDOW;

    // For window times, scheduling.start/end are the canonical API field names;
    // scheduling.windowStart/windowEnd are copies kept for legacy compatibility.
    const windowStart = mergedData.windowStart ?? scheduling.windowStart ?? (isWindow ? scheduling.start : undefined);
    const windowEnd = mergedData.windowEnd ?? scheduling.windowEnd ?? (isWindow ? scheduling.end : undefined);
    const dailyStart =
        mergedData.dailyStart ?? scheduling.dailyStart ?? (scheduleType === DAILY ? scheduling.start : undefined);

    return {
        scheduleType,
        interval: mergedData.interval ?? scheduling.interval,
        isWindow,
        windowStart,
        windowEnd,
        dailyStart,
    };
}

/**
 * Determine schedule type label for error messages.
 * Matches portal/ui's TestScheduleHelper.getSaveType().
 * @param {{ scheduleType: string, isWindow: boolean }} schedule
 * @returns {string}
 */
function getSaveType(schedule) {
    if (schedule.scheduleType === DAILY) return DAILY;
    if (schedule.isWindow) return WINDOW;
    return INTERVAL;
}

/**
 * Returns an error message if agent selection is invalid, null otherwise.
 * Matches portal/ui's validateTest() lines 934-941.
 * @param {Object} mergedData
 * @returns {string|null}
 */
function validateAgentSelection(mergedData) {
    const agents = mergedData?.agents || {};
    if (isEmpty(agents.ids) && isEmpty(agents.tags) && !agents.all && mergedData.adapterType !== MULTI) {
        return t`Invalid Test. Please select at least one nPoint or nPoint tag`;
    }
    return null;
}

/**
 * Returns an error message if BTT user credentials are missing, null otherwise.
 * Only validates when "Run As User" is enabled and "Use Default Login" is unchecked.
 * Matches portal/ui's validateTest() lines 948-966.
 * @param {Object} mergedData
 * @param {React.RefObject} bttStateRef
 * @returns {string|null}
 */
function validateBttCredentials(mergedData, bttStateRef) {
    if (!bttStateRef?.current) return null;
    const { runAsUser } = bttStateRef.current;
    const bttUserDefaultLogin = get(mergedData, 'bttUser.bttUserDefaultLogin', false);
    if (!runAsUser || bttUserDefaultLogin) return null;

    if (isEmpty(get(mergedData, 'bttUser.bttUsername', ''))) {
        return t`Username required to run script as non-default user`;
    }
    // NgpPasswordField stores its value at bttUser.bttUserPassword.value;
    // API-loaded data may store it directly at bttUser.bttUserPassword as a string.
    const password = get(mergedData, 'bttUser.bttUserPassword.value') ?? get(mergedData, 'bttUser.bttUserPassword');
    if (!isSupplied(password)) {
        return t`Password required to run script as non-default user`;
    }
    return null;
}

/**
 * Returns an error message if VoIP Pulse callee instructions are incomplete, null otherwise.
 * callVia.head is only set when the Callee Instructions section is active,
 * so this is a no-op for non-VoIP and phone-type tests.
 * @param {Object} mergedData
 * @returns {string|null}
 */
function validateCalleeInstructions(mergedData) {
    const callVia = mergedData?.testConfig?.callVia;
    if (callVia?.head !== undefined && (isEmpty(callVia.head) || isEmpty(callVia.body))) {
        return t`Callee Instructions are required`;
    }
    return null;
}

/**
 * Returns an error message if the schedule has no populated fields, null otherwise.
 * Matches portal/ui's isSchedulePopulated check (validateSchedule lines 978-990).
 * @param {{ scheduleType: string, isWindow: boolean, interval: *, windowStart: *, windowEnd: *, dailyStart: * }} schedule
 * @returns {string|null}
 */
function validateSchedulePopulated(schedule) {
    const hasInterval = isSupplied(schedule.interval);
    const hasWindowTimes = isSupplied(schedule.windowStart) && isSupplied(schedule.windowEnd);
    const hasDailyStart = isSupplied(schedule.dailyStart);
    if (hasInterval || hasWindowTimes || hasDailyStart) return null;

    const saveType = getSaveType(schedule);
    if (saveType === DAILY) return t`A Start Time is required`;
    if (saveType === WINDOW) return t`An Interval, Start, and End Time are required`;
    return t`An Interval is required`;
}

/**
 * Returns an error message if window schedule settings are invalid, null otherwise.
 * Covers isWindowPopulated (4b), isStartEndOk (4c), and isDurationOk (4d).
 * Matches portal/ui's validateSchedule() lines 991-1001.
 * @param {{ isWindow: boolean, interval: *, windowStart: *, windowEnd: * }} schedule
 * @returns {string|null}
 */
function validateWindowSchedule(schedule) {
    if (!schedule.isWindow) return null;
    if (
        !isSupplied(schedule.interval) ||
        schedule.interval <= 0 ||
        !isSupplied(schedule.windowStart) ||
        !isSupplied(schedule.windowEnd)
    ) {
        return t`Please supply an Interval, Start, and End Time`;
    }
    const duration = getDurationInSeconds(schedule.windowStart, schedule.windowEnd);
    if (duration > DAY_IN_SECONDS) {
        return t`Start Time and End Time must be less than a day`;
    }
    if (isSupplied(schedule.interval) && schedule.interval > Math.abs(duration)) {
        return t`The Interval cannot be larger than the Start/End window`;
    }
    return null;
}

/**
 * Shared validation hook for service test forms.
 * Validates agent selection, BTT user credentials, VoIP callee instructions,
 * and schedule configuration before save/verify.
 * Used by both TestDetailActionButtons (toolbar) and TestDetailBottomButtons.
 *
 * Matches portal/ui's validateTest() + validateSchedule() flow from
 * testsDetails.controller.js lines 933-1001.
 *
 * @param {boolean} groupExist - Service type exist or not
 * @param {Object} formData - Current form data from InfrastructureDetailBase
 * @param {React.RefObject} [bttStateRef] - Ref tracking BTT toggle states (runAsUser, defaultLogin)
 * @returns {Function} validateTest - Higher-order function wrapping submit/verify callbacks
 */
export function useTestValidation(groupExist, formData, bttStateRef) {
    const isNewRecord = isEmpty(formData?._id);
    const [, uiAlertActions] = useUiAlerts();

    return useCallback(
        (fn) => (submitData) => {
            if (!groupExist) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, t`Invalid Test. Test type not found.`));
                return;
            }

            // Deep merge preserves nested metadata (metricIds, traceRoute.target, _id, etc.)
            // that shallow spread would overwrite with incomplete submitData.
            const mergedData = isNewRecord ? submitData : deepMergeForSave(formData, submitData);

            const agentError = validateAgentSelection(mergedData);
            if (agentError) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, agentError));
                return;
            }

            const bttError = validateBttCredentials(mergedData, bttStateRef);
            if (bttError) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, bttError));
                return;
            }

            const calleeError = validateCalleeInstructions(mergedData);
            if (calleeError) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, calleeError));
                return;
            }

            // Skip schedule validation for Wi-Fi Group Test children — their schedule is inherited
            // from the parent at runtime. (portal/ui never hits this because prepareScheduleForScreen
            // always initializes interval=600; React's shouldUnregister loses flat schedule fields
            // when ScheduleFields unmounts for 'multi'.)
            if (mergedData.adapterType === MULTI) return fn(mergedData);

            const schedule = resolveScheduleState(mergedData);
            const scheduleError = validateSchedulePopulated(schedule) ?? validateWindowSchedule(schedule);
            if (scheduleError) {
                uiAlertActions.createAlert(createAlertConfig(WARNING, scheduleError));
                return;
            }

            return fn(mergedData);
        },
        [groupExist, isNewRecord, formData, bttStateRef, uiAlertActions]
    );
}
